import React, { useContext, useState, useEffect } from "react";
import { auth } from "../firebase";
import firebase from "firebase/compat/app";
import {
        getUserData,
        createUserDocument,
        ensureUserDocument,
        upsertUserDevice,
        updateUserDeviceProfile,
} from "../database/firestore";
import { getDeviceFingerprint } from "../utils/device";
import i18n from "../i18n";

// Initialize context with default empty values
const AuthContext = React.createContext({
	currentUser: null,
	userData: null,
        login: () => Promise.resolve(),
        signup: () => Promise.resolve(),
        logout: () => Promise.resolve(),
        resetPassword: () => Promise.resolve(),
        updateEmail: () => Promise.resolve(),
        updatePassword: () => Promise.resolve(),
        signInWithGoogle: () => Promise.resolve(),
        adminSignInWithGoogle: () => Promise.resolve(),
        signInWithCustomToken: () => Promise.resolve(),
        completeProfile: () => Promise.resolve(),
        sendEmailVerification: () => Promise.resolve(),
        resendEmailVerification: () => Promise.resolve(),
        isEmailVerified: () => Promise.resolve(false),
        reloadUser: () => Promise.resolve(),
});

export function useAuth() {
	return useContext(AuthContext);
}

export function AuthProvider({ children }) {
	const [currentUser, setCurrentUser] = useState();
	const [userData, setUserData] = useState(null);
	const [loading, setLoading] = useState(true);

        async function signup(email, password) {
                const credential = await auth.createUserWithEmailAndPassword(email, password);
                try {
                        if (credential.user?.uid) {
                                // Send verification email immediately after signup
                                await credential.user.sendEmailVerification();

                                // Initialize user document but don't initialize trial yet
                                // Trial will be initialized after email verification
                                await ensureUserDocument(
                                        credential.user.uid,
                                        credential.user.email || email,
                                        false, // Don't initialize trial yet
                                        null // No tier selection for eartag users
                                );
                        }
                } catch (error) {
                        console.error("Error during signup setup:", error);
                        // Clean up the partially created auth user to prevent inconsistent state
                        try {
                                await credential.user?.delete();
                        } catch (deleteError) {
                                console.error("Failed to cleanup partial signup:", deleteError);
                        }
                        throw new Error("Failed to complete account setup. Please try again.");
                }
                return credential;
        }

        async function login(email, password, selectedRace) {
                const credential = await auth.signInWithEmailAndPassword(email, password);

                try {
                        // Check if email is verified
                        if (!credential.user.emailVerified) {
                                // Return verification status but don't throw error
                                // Let the UI handle the verification prompt
                                return { 
                                        userCredential: credential, 
                                        emailVerified: false,
                                        needsVerification: true 
                                };
                        }

                        // If email is verified, ensure user document and initialize trial if needed
                        if (credential.user.emailVerified) {
                                try {
                                        // Get selected tier from user document if available
                                        let selectedTier = null;
                                        try {
                                                const { getUserData } = await import("../database/firestore");
                                                const userData = await getUserData(credential.user.uid);
                                                selectedTier = userData?.SelectedBillingTier || null;
                                        } catch (error) {
                                                console.error("Error fetching user data for tier:", error);
                                        }
                                        
                                        // Ensure user document exists (will initialize trial if email verified)
                                        await ensureUserDocument(
                                                credential.user.uid,
                                                credential.user.email || email,
                                                true, // Initialize trial if email verified
                                                selectedTier // Pass selected tier
                                        );
                                } catch (error) {
                                        console.error("Error ensuring user document during login:", error);
                                }
                        }

                        const { deviceId, deviceName, userAgent } = getDeviceFingerprint();

                        const result = await upsertUserDevice(credential.user.uid, {
                                deviceId,
                                name: deviceName,
                                userAgent,
                                email: credential.user.email || email,
                        });

                        return { 
                                userCredential: credential, 
                                emailVerified: true,
                                ...result 
                        };
                } catch (error) {
                        console.error("Error updating device profile during login:", error);
                        return { 
                                userCredential: credential,
                                emailVerified: credential.user?.emailVerified || false
                        };
                }
        }

	function logout() {
		return auth.signOut();
	}

	async function resetPassword(email) {
		if (!email) {
			throw new Error("Email is required");
		}

		try {
			await auth.sendPasswordResetEmail(email);
		} catch (error) {
			console.error("Error sending password reset email:", error);
			throw error;
		}
	}

        async function updateEmail(email) {
                const user = auth.currentUser;

                if (!user) {
                        throw new Error("No authenticated user");
                }

                if (user.email === email) {
                        return;
                }

                await user.updateEmail(email);

                setCurrentUser(user);
                setUserData((previous) =>
                        previous ? { ...previous, Email: email } : previous
                );
        }

	function updatePassword(email) {
		return auth.sendPasswordResetEmail(email);
	}

	// New function for Google sign-in
	function signInWithGoogle() {
		const provider = new firebase.auth.GoogleAuthProvider();
		return auth.signInWithPopup(provider);
	}

	// Special admin sign-in that checks for @biocv.org domain
	async function adminSignInWithGoogle() {
		const provider = new firebase.auth.GoogleAuthProvider();

		try {
			const result = await auth.signInWithPopup(provider);
			const user = result.user;

                        // Check if the email has the @biocv.org domain
                        if (!user?.email || !user.email.endsWith("@biocv.org")) {
                                // Sign out if not @biocv.org domain
                                await auth.signOut();
                                throw new Error(
                                        "Unauthorized domain. Admin access requires @biocv.org email."
                                );
			}

			// Get the user data if it exists
			const userDoc = await getUserData(user.uid);

			// Check if userDoc exists and if its Role property is not 'admin'
			// @ts-ignore
			if (!userDoc || !userDoc.Role || userDoc.Role !== "admin") {
				// Create or update user document with admin role
				await createUserDocument(user.uid, {
					email: user.email,
					displayName: user.displayName || "",
					role: "admin",
					betaUser: false,
				});
			}

			return result;
		} catch (error) {
			console.error("Admin Google sign-in error:", error);
			throw error;
		}
	}

	// Function to sign in with a custom token
	async function signInWithCustomToken(token) {
		try {
			const result = await auth.signInWithCustomToken(token);
			return result;
		} catch (error) {
			console.error("Custom token sign-in error:", error);
			throw error;
		}
	}

	useEffect(() => {
		const unsubscribe = auth.onAuthStateChanged(async (user) => {
			// Ensure state is set to undefined if user is null, otherwise set the user object.
			// @ts-ignore
			setCurrentUser(user ? user : undefined);

			if (user) {
				let userData = await getUserData(user.uid);
				setUserData(userData);
			}

			setLoading(false);
		});

		return unsubscribe;
	}, []);

	// Sync language preference from userData with i18n
	useEffect(() => {
		if (!userData || !userData.Language) {
			return;
		}

		const userLanguage = userData.Language;
		const currentLanguage = i18n.language || i18n.languages?.[0] || "en";

		// Only change language if it differs from current language
		if (userLanguage && userLanguage !== currentLanguage) {
			try {
				i18n.changeLanguage(userLanguage);
				// Update localStorage to keep it in sync
				localStorage.setItem("i18nextLng", userLanguage);
			} catch (error) {
				console.error("[AuthContext][language-sync] - Error changing language:", error);
			}
		} else if (userLanguage && userLanguage === currentLanguage) {
			// Even if language matches, ensure localStorage is in sync
			try {
				const storedLanguage = localStorage.getItem("i18nextLng");
				if (storedLanguage !== userLanguage) {
					localStorage.setItem("i18nextLng", userLanguage);
				}
			} catch (error) {
				console.error("[AuthContext][language-sync] - Error updating localStorage:", error);
			}
		}
	}, [userData, userData?.Language]);

        async function completeProfile(customName, rank) {
                if (!currentUser) {
                        throw new Error("No authenticated user");
                }

                const { deviceId } = getDeviceFingerprint();

                const result = await updateUserDeviceProfile(currentUser.uid, deviceId, {
                        customName,
                        rank,
                });

                setUserData((prev) => {
                        if (!prev) {
                                return prev;
                        }

                        const updatedDevices = Array.isArray(prev.Devices)
                                ? prev.Devices.map((device) =>
                                          device.DeviceId === result.deviceRecord.DeviceId
                                                  ? result.deviceRecord
                                                  : device
                                  )
                                : [];

                        return {
                                ...prev,
                                Devices: updatedDevices,
                                ProfileComplete: result.profileComplete,
                        };
                });

                return result;
        }

        /**
         * Send email verification to the current user
         * @returns {Promise<void>}
         */
        async function sendEmailVerification() {
                const user = auth.currentUser;
                if (!user) {
                        throw new Error("No authenticated user");
                }
                
                if (user.emailVerified) {
                        throw new Error("Email already verified");
                }
                
                await user.sendEmailVerification();
        }

        /**
         * Resend email verification (alias for sendEmailVerification)
         * @returns {Promise<void>}
         */
        async function resendEmailVerification() {
                return sendEmailVerification();
        }

        /**
         * Check if current user's email is verified
         * @returns {Promise<boolean>}
         */
        async function isEmailVerified() {
                const user = auth.currentUser;
                if (!user) {
                        return false;
                }
                
                // Reload user to get latest verification status
                await user.reload();
                return user.emailVerified;
        }

        /**
         * Reload current user data (useful after email verification)
         * @returns {Promise<void>}
         */
        async function reloadUser() {
                const user = auth.currentUser;
                if (!user) {
                        return;
                }
                await user.reload();
                setCurrentUser(user);
        }

        const value = {
                currentUser,
                userData,
                login,
                signup,
                logout,
                resetPassword,
                updateEmail,
                updatePassword,
                signInWithGoogle,
                adminSignInWithGoogle,
                signInWithCustomToken,
                completeProfile,
                sendEmailVerification,
                resendEmailVerification,
                isEmailVerified,
                reloadUser,
        };

	return (
		<AuthContext.Provider value={value}>
			{!loading && children}
		</AuthContext.Provider>
	);
}
